import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  useTheme,
  Divider,
  Button
} from "@mui/material";
import { getRiskLevel, calcRiskOnlyScore, calcPerformanceGrade } from "utils/riskScore";
import { RiskLabel } from "components/mas/Label";
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
  Area,
  AreaChart,
  ReferenceLine
} from "recharts";
import { LanguageContext } from "../../../../context/LanguageContext";
import { COLORS } from "theme/tokens";
import { toSeries, round2, buildEquityBars, formatUsd, formatSigned, formatAxisUsd, axisUnitFor } from "utils/equityBaseline";

const ORANGE_PRIMARY = "#f16b2a";
const ORANGE_DARK = COLORS.main;
const ORANGE_LIGHT = "rgba(241, 107, 42, 0.1)";

// subtitle：標題下的說明區塊（目前只有「本金變化曲線」用，放說明文字＋圖例）；沒傳就跟原本完全一樣。
const ModernChartCard = ({ title, subtitle, children, height = 350, autoHeight = false, showHeader = true }) => {
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        bgcolor: COLORS.white,
        borderRadius: "16px",
        boxShadow: "0 4px 12px 0 rgba(0,0,0,0.03)",
        border: "1px solid rgba(0,0,0,0.05)",
      }}
    >
      <CardContent sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
        {showHeader && (
          <Box display="flex" alignItems="center" mb={subtitle ? 0.75 : 2}>
            <Box sx={{ width: 4, height: 18, bgcolor: ORANGE_PRIMARY, mr: 1.5, borderRadius: "2px" }} />
            <Typography variant="h6" fontWeight="bold" color="dark">
              {title}
            </Typography>
          </Box>
        )}
        {showHeader && subtitle && <Box mb={2}>{subtitle}</Box>}
        {autoHeight ? (
          <Box flexGrow={1}>{children}</Box>
        ) : (
          <Box flexGrow={1} minHeight={height}>
            <ResponsiveContainer width="100%" height="100%">
              {children}
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Card sx={{ p: 1.5, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", borderRadius: "10px" }}>
        <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={0.5}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Box key={index} display="flex" alignItems="center" gap={1} mt={0.2}>
            <Box width={8} height={8} borderRadius="50%" bgcolor={entry.color} />
            <Typography variant="button" fontWeight="bold" color="dark">
              {entry.name}: {entry.value}
            </Typography>
          </Box>
        ))}
      </Card>
    );
  }
  return null;
};

// 2026-09-29（A10）本金變化曲線的柱色。位置（本金線之上／之下）才是主要訊號，顏色只是輔助：
//   高於或等於本金用主色、低於用 error 紅 —— 與同卡「總報酬率」的正負色一致（正值主色、負值 error）。
//   拿不到初始本金 → 中性灰，不用獲利色，避免暗示有賺。中性灰用 grayDark（白底約 3.5:1，達圖形物件 3:1）；
//   grayMedian 只有約 1.7:1，柱子會淡到看不清楚。
//   ⚠️ 待 Sean 決定：另一個選項是 COLORS.safe 綠／COLORS.error 紅（表現徽章的慣例），但台灣使用者有「紅漲綠跌」的反向直覺。
//     另外主色橘與 error 紅亮度對比只有約 1.4:1，紅綠色盲模擬下幾乎同色 —— 目前靠「位置」（本金線之上／之下）區分，
//     圖例文字也寫明高於／低於；要換色只改這裡一處（圖例跟著變）。
const EQUITY_TONE_COLOR = {
  above: COLORS.main,
  below: COLORS.error,
  neutral: COLORS.grayDark,
};

// 本金變化曲線的說明文字、圖例文字顏色：用黑色。說明是唯一寫明「不含浮動損益」「顏色不代表當期賺賠」的地方，
//   灰字 grayDark 在白底只有約 3.5:1（12px 小字要 4.5:1）；同 repo 的 StopLossNotice 也是因此改黑字。
const EQUITY_TEXT_COLOR = COLORS.black;

// 本金變化曲線專屬 tooltip。共用的 CustomTooltip 會顯示「value: 6792.4」（dataKey 當名稱、沒有單位、沒跟本金比）。
//   → 「2021｜● 餘額 6,792.40 USD／相對本金 −3,207.60（−32.08%）／本期 +4,822.20」
//   % 用同一桶的累積報酬，與上一張圖同一個數字。
//   「本期」是這一桶的已實現損益：柱子的位置與顏色表示「期末餘額相對初始本金」，虧損後回升的年份仍是紅柱，
//   不列本期損益會被讀成「這一年賠錢」（審查 A10-1：dev DB 47 根柱有 6 根顏色跟當期賺賠相反）。
const EquityTooltip = ({ active, payload, label, texts }) => {
  const bar = active && payload && payload.length ? payload[0]?.payload : null;
  if (!bar) return null;
  return (
    <Card sx={{ p: 1.5, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", borderRadius: "10px" }}>
      <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={0.5}>
        {label}
      </Typography>
      <Box display="flex" alignItems="center" gap={1} mt={0.2}>
        <Box width={8} height={8} borderRadius="50%" bgcolor={EQUITY_TONE_COLOR[bar.tone]} />
        <Typography variant="button" fontWeight="bold" color="dark" sx={{ textTransform: "none" }}>
          {texts.balance} {formatUsd(bar.balance)} USD
        </Typography>
      </Box>
      {bar.diff !== null && (
        <Typography variant="caption" color="dark" display="block" sx={{ mt: 0.3, pl: 2 }}>
          {texts.vsCapital
            .replace("{diff}", formatSigned(bar.diff))
            .replace("{pct}", formatSigned(bar.pct, { suffix: "%", signOf: bar.diff }))}
        </Typography>
      )}
      {bar.change !== null && bar.change !== undefined && (
        <Typography variant="caption" color="dark" display="block" sx={{ mt: 0.3, pl: 2 }}>
          {texts.periodChange.replace("{change}", formatSigned(bar.change))}
        </Typography>
      )}
    </Card>
  );
};

// 本金變化曲線的說明＋圖例。圖例刻意不畫進繪圖區（ReferenceLine 的 label）：
//   柱子都從本金線長出去，線上的文字一定會壓到柱子；放在標題下方才不會跟資料搶位置。
const EquitySubtitle = ({ caption, legend }) => (
  <>
    <Typography variant="caption" sx={{ display: "block", color: EQUITY_TEXT_COLOR, lineHeight: 1.6 }}>
      {caption}
    </Typography>
    {legend && (
      <Box display="flex" flexWrap="wrap" alignItems="center" columnGap={2} rowGap={0.5} mt={0.75}>
        <Box display="flex" alignItems="center" gap={0.75}>
          <Box sx={{ width: 18, borderTop: `2px dashed ${COLORS.grayDark}` }} />
          <Typography variant="caption" sx={{ color: EQUITY_TEXT_COLOR }}>{legend.baseline}</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.75}>
          <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: EQUITY_TONE_COLOR.above }} />
          <Typography variant="caption" sx={{ color: EQUITY_TEXT_COLOR }}>{legend.above}</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.75}>
          <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: EQUITY_TONE_COLOR.below }} />
          <Typography variant="caption" sx={{ color: EQUITY_TEXT_COLOR }}>{legend.below}</Typography>
        </Box>
      </Box>
    )}
  </>
);

// 報表序列 → 圖表列。toSeries 兩種形狀都吃（陣列／Beast 舊資料的 Python 原始物件），
//   舊寫法直接 .map 物件會丟 TypeError。缺值維持舊行為記為 0。
const toChartRows = (raw, innerKey) =>
  toSeries(raw, innerKey).map(({ key, value }) => ({
    name: key,
    value: Number.isFinite(value) ? round2(value) : 0,
  }));

const StatRow = ({ label, value, unit = '', color = 'dark' }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" py={1.2}>
    <Typography variant="button" color="text.secondary" fontWeight="medium">
      {label}
    </Typography>
    <Box>
      <Typography variant="h5" component="span" color={color} fontWeight="bold">
        {value}
      </Typography>
      {unit && (
        <Typography variant="button" component="span" color={color} fontWeight="bold" sx={{ ml: 0.5 }}>
          {unit}
        </Typography>
      )}
    </Box>
  </Box>
);

// showBankruptNotice：只有 AI 助理頁（使用者剛跑完自己的回測）才開。
//   策略市集的報告是別人上架的（「您設定的手數」對看的人不成立）、Beast 是 AI 迭代的參數，
//   而策略管理/市集存的舊報告若早於 2026-05-20，當時根本沒有爆倉保護機制，說明會是錯的。
const StrategyKPICharts = ({ data, showBankruptNotice = false }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState("performance");
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);

  useEffect(() => {
    loadLanguageFile("StrategyKPICharts", "strategy/StrategyKPICharts/StrategyKPICharts_lang");
  }, [language, loadLanguageFile]);

  const LANGUAGE = componentLanguageData.StrategyKPICharts || { Main: {} };
  const result = data?.formattedResult || data;

  if (!result || !result["performance_metrics"]) return null;

  const kpi = result["performance_metrics"];
  const winLoss = result["win_loss_ratio"] || {};

  // Parse charts data
  const returnData = toChartRows(result["cumulative_return_line_chart"], "cumulative_return_percent");
  const maxdrawdownData = toChartRows(result["max_drawdown_trend"], "max_drawdown_percent");

  // 2026-09-29（A10）本金變化曲線：annual_pnl_usd 是每期期末的「已平倉餘額」，不是損益。
  //   柱子改從初始本金基準線起算（recharts 範圍柱 [本金, 餘額]）：高於本金往上、低於本金往下，
  //   解掉「虧到只剩 5,100 看起來像賺了 5,100」。初始本金從報表反推（utils/equityBaseline.js），
  //   拿不到就退回舊畫法（從 0 起算）但一律中性灰、不畫基準線。
  //   沒有任何柱（0 筆已平倉交易）→ 說明改講「沒有交易」，不要答非所問地說「無法確認初始本金」。
  const equity = buildEquityBars(result);
  const hasEquityBase = equity.capital !== null;
  const equityCapitalText = hasEquityBase ? formatUsd(equity.capital, { dropZeroCents: true }) : "";
  const equityCaption = !equity.bars.length
    ? (LANGUAGE.Main.EquityCaptionEmpty || "這份報告沒有已平倉的交易，沒有餘額可以畫。")
    : hasEquityBase
    ? (LANGUAGE.Main.EquityCaption ||
        "不含未平倉部位的浮動損益。柱子從初始本金起算：往上＝期末餘額高於本金、往下＝低於本金；顏色只表示相對初始本金的位置，不代表該期賺賠（該期損益見提示框）。")
    : (LANGUAGE.Main.EquityCaptionNoBase ||
        "不含未平倉部位的浮動損益。這份報告無法確認初始本金，柱子從 0 起算、不區分高於或低於本金。");
  const equityLegend = hasEquityBase
    ? {
        baseline: (LANGUAGE.Main.EquityBaseline || "初始本金 {capital} USD").replace("{capital}", equityCapitalText),
        above: LANGUAGE.Main.EquityLegendAbove || "高於本金",
        below: LANGUAGE.Main.EquityLegendBelow || "低於本金",
      }
    : null;
  const equityAxisUnit = axisUnitFor([...equity.bars.map((b) => b.balance), equity.capital]);
  const equityTooltipTexts = {
    balance: LANGUAGE.Main.EquityBalance || "餘額",
    vsCapital: LANGUAGE.Main.EquityVsCapital || "相對本金 {diff}（{pct}）",
    periodChange: LANGUAGE.Main.EquityPeriodChange || "本期 {change}",
  };

  const winRate = parseFloat(winLoss["win_rate"] || 0);
  const winLossData = [
    { name: LANGUAGE.Main.Content1, value: winRate },
    { name: LANGUAGE.Main.Content2, value: parseFloat(winLoss["loss_rate"] || 0) }
  ];

  const labelMap = {
    trades: LANGUAGE.Main.Content3,
    winRate: LANGUAGE.Main.Content4,
    maxDrawdownPct: LANGUAGE.Main.Content5,
    profitLossRatio: LANGUAGE.Main.Content6,
    returnRatePct: LANGUAGE.Main.Content7,
    cagr: LANGUAGE.Main.Content13,
    sharpeRatio: LANGUAGE.Main.Content14,
    sortinoRatio: LANGUAGE.Main.Content15,
    maxConsecutiveLosses: LANGUAGE.Main.Content16,
    maxSingleLoss: LANGUAGE.Main.Content17,
    profitFactor: LANGUAGE.Main.Content18,
    profitAmount: "Profit (USD)"
  };

  const getKpiValue = (key) => {
    let val = 0;
    if (key === 'profitAmount') {
      val = result?.["general_data"]?.["total_profit_loss"];
    } else {
      const mapping = {
        trades: 'number_of_trades',
        winRate: 'win_rate',
        maxDrawdownPct: 'max_drawdown',
        returnRatePct: 'total_return_rate',
        sharpeRatio: 'sharpe_ratio',
        sortinoRatio: 'sortino_ratio',
        profitFactor: 'profit_factor',
        maxConsecutiveLosses: 'max_consecutive_losses',
        maxSingleLoss: 'max_single_loss',
        profitLossRatio: 'profit_loss_ratio',
        cagr: 'cagr'
      };
      val = kpi[key] ?? kpi[mapping[key]];
    }
    return (val === null || val === undefined || isNaN(Number(val))) ? 0 : Number(val);
  };

  // 方案 B（2026-09-21 試驗、2026-09-22 採用）：風險 與 表現 分開算。
  //   直接吃原始 performance_metrics（kpi，snake_case），不經過 getKpiValue ——
  //   getKpiValue 會把缺值變成 0，而 0 在這裡有意義（回撤 0 = 看起來很安全、報酬 0 = 不虧），
  //   缺值應該是「不確定」(新函式內部給 50 分)，不該被當成最好的情況。
  const riskOnlyScore = calcRiskOnlyScore(kpi);
  const riskOnlyInfo = getRiskLevel(riskOnlyScore);
  const perf = calcPerformanceGrade(kpi);

  // 表現徽章：只給字不給分數 —— 風險是「分數越高越糟」，表現若也給分數會是「越高越好」，
  // 同一排兩個方向相反的數字會讓人搞混。
  // 字色：淺底一律深字（白字在綠/黃/橘底只有 1.7~2.4:1），只有虧損用紅底白字(4.21:1)。
  const PERF_COLOR = {
    excellent: COLORS.safe,
    good: COLORS.safe,
    fair: COLORS.warning,
    poor: COLORS.riskHigh,
    loss: COLORS.error,
  };
  const PERF_TEXT_COLOR = {
    excellent: COLORS.black,
    good: COLORS.black,
    fair: COLORS.black,
    poor: COLORS.black,
    loss: COLORS.white,
  };
  const PERF_LABEL = {
    excellent: LANGUAGE.Main.PerfExcellent || "優",
    good: LANGUAGE.Main.PerfGood || "良",
    fair: LANGUAGE.Main.PerfFair || "普通",
    poor: LANGUAGE.Main.PerfPoor || "差",
    loss: LANGUAGE.Main.PerfLoss || "虧損",
  };
  const verdictCardSx = {
    width: "100%",
    height: "100%",
    bgcolor: COLORS.white,
    borderRadius: "16px",
    p: 1.75,
    px: 3,
    boxShadow: "0 4px 12px 0 rgba(0,0,0,0.03)",
    border: "1px solid rgba(0,0,0,0.05)",
  };

  // 💥 2026-09-21 爆倉說明。引擎的 Bankruptcy Guard（mas/backtest/backtest_engine.py:392-412）
  //   在「已平倉」累計虧損跨過 -100% 本金時觸發：之後的交易**仍然存在**，只是損益記為 0
  //   （仍計入交易筆數與勝率分母）；跨過的那一筆保留全額虧損，所以總報酬可以低於 -100%（實例 -108.93%）。
  //   下方圖表的 x 軸是用「交易平倉時間」分桶（backtest_engine.py:444-455），不是日曆：
  //     · 爆倉後策略若還繼續交易 → 曲線是一條平線，畫到最後一筆
  //     · 策略自己停手（例如停損出場後不再進場）→ 圖就停在最後一筆，看起來像「沒跑完」
  //   而回測本身一定跑完區間內所有 K 棒（history_data_db.py 的迴圈無條件跑完，爆倉保護是事後處理）。
  //   判斷用「< -100」而不是「≤」，與引擎的嚴格小於（cum_pnl < -capital）一致。
  //   仍是近似：總報酬四捨五入到小數兩位，且保護只看已實現損益、不看浮動損益（MTM）——
  //   「沒出現這則說明」不代表帳戶撐得過途中的浮虧。
  //   getKpiValue 缺值回 0 → 不會誤觸發。
  const isBankrupt = showBankruptNotice && getKpiValue("returnRatePct") < -100;

  return (
    <Box py={1}>
      <Grid container spacing={3}>
        {/* Row 0：爆倉說明（見 isBankrupt 的註解）。放最上方，讓使用者先知道下方圖表為何會提早結束。 */}
        {isBankrupt && (
          <Grid item xs={12}>
            <Box
              sx={{
                px: 3,
                py: 2,
                borderRadius: "16px",
                border: "1px solid rgba(214, 58, 38, 0.35)",
                bgcolor: "rgba(214, 58, 38, 0.06)",
              }}
            >
              <Typography variant="button" fontWeight="bold" sx={{ display: "block", color: COLORS.red, mb: 0.5, textTransform: "none" }}>
                {LANGUAGE.Main.BankruptTitle || "💥 帳戶在回測期間已爆倉"}
              </Typography>
              <Typography variant="caption" sx={{ display: "block", color: COLORS.grayDark, lineHeight: 1.7 }}>
                {LANGUAGE.Main.BankruptDesc ||
                  "累計虧損超過本金後，之後的交易不再計入損益，所以下方圖表只畫到最後一筆交易 —— 回測本身有跑完您設定的整個區間。這通常代表手數相對本金太大（槓桿過高）。"}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Row 1（方案 B，2026-09-22 採用）：風險 與 表現 拆成兩張卡並排。
            舊版只有一張「策略風險評估」，把回撤(風險)與 Sharpe/PF/勝率(表現)混成一個分數，
            導致「每筆都虧但虧得慢」的策略被標成中風險。拆開後兩者各說各的，
            「低風險・虧損」就能如實呈現。公式見 utils/riskScore.js 的方案 B 區段。 */}
        {riskOnlyInfo && (
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={verdictCardSx}>
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
                <Box sx={{ width: 4, height: 18, bgcolor: ORANGE_PRIMARY, borderRadius: "2px" }} />
                <Typography variant="button" fontWeight="bold" color="dark" sx={{ whiteSpace: "nowrap" }}>
                  {LANGUAGE.Main.RiskTitle || "風險"}
                </Typography>
                <RiskLabel
                  level={riskOnlyInfo.level}
                  score={riskOnlyScore}
                  label={riskOnlyInfo.level === "low" ? LANGUAGE.Main.Content21 : riskOnlyInfo.level === "medium" ? LANGUAGE.Main.Content22 : LANGUAGE.Main.Content23}
                  scoreUnit={LANGUAGE.Main.Content24 || "分"}
                />
              </Box>
              <Typography variant="caption" sx={{ display: "block", mt: 1, color: COLORS.grayDark, lineHeight: 1.6 }}>
                {LANGUAGE.Main.RiskDesc || "虧損時可能有多痛（最大回撤、最大連續虧損）"}
              </Typography>
            </Card>
          </Grid>
        )}
        {perf && (
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={verdictCardSx}>
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
                <Box sx={{ width: 4, height: 18, bgcolor: ORANGE_PRIMARY, borderRadius: "2px" }} />
                <Typography variant="button" fontWeight="bold" color="dark" sx={{ whiteSpace: "nowrap" }}>
                  {LANGUAGE.Main.PerfTitle || "表現"}
                </Typography>
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    height: 28,
                    px: 1.5,
                    borderRadius: "6px",
                    fontWeight: "bold",
                    fontSize: "0.875rem",
                    whiteSpace: "nowrap",
                    color: PERF_TEXT_COLOR[perf.grade],
                    bgcolor: PERF_COLOR[perf.grade],
                  }}
                >
                  {PERF_LABEL[perf.grade]}
                </Box>
              </Box>
              <Typography variant="caption" sx={{ display: "block", mt: 1, color: COLORS.grayDark, lineHeight: 1.6 }}>
                {perf.grade === "loss"
                  ? (LANGUAGE.Main.PerfDescLoss || "回測期間總報酬為負")
                  : (LANGUAGE.Main.PerfDesc || "扣掉風險之後賺得好不好（Sharpe、獲利因子、年化報酬）")}
              </Typography>
            </Card>
          </Grid>
        )}

        {/* Row 2: Dashboard & Donut */}
        <Grid item xs={12} md={7}>
          <ModernChartCard title={LANGUAGE.Main.Content8} autoHeight>
            {/* Tab Switcher */}
            <Box
              sx={{
                display: "flex",
                bgcolor: COLORS.grayLightest,
                p: 0.5,
                borderRadius: "12px",
                mb: 3
              }}
            >
              {[
                { id: "performance", label: "Performance" },
                { id: "risk", label: "Risk" },
                { id: "details", label: "Details" }
              ].map((tab) => (
                <Button
                  key={tab.id}
                  fullWidth
                  onClick={() => setActiveTab(tab.id)}
                  sx={{
                    borderRadius: "10px",
                    bgcolor: activeTab === tab.id ? COLORS.white : "transparent",
                    color: activeTab === tab.id ? "dark" : "#8392ab",
                    boxShadow: activeTab === tab.id ? "0 2px 8px rgba(0,0,0,0.08)" : "none",
                    fontWeight: "bold",
                    textTransform: "none",
                    "&:hover": { bgcolor: activeTab === tab.id ? COLORS.white : "rgba(0,0,0,0.02)" }
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </Box>

            {/* Tab Content */}
            <Box px={1}>
              {activeTab === "performance" && (
                <Fade in timeout={400}>
                  <Box>
                    <StatRow label={labelMap.returnRatePct} value={getKpiValue('returnRatePct').toFixed(2)} unit="%" color={getKpiValue('returnRatePct') >= 0 ? ORANGE_PRIMARY : "error"} />
                    <Divider />
                    <StatRow label={labelMap.cagr} value={getKpiValue('cagr').toFixed(2)} unit="%" color={getKpiValue('cagr') >= 0 ? ORANGE_PRIMARY : "error"} />
                    <Divider />
                    <StatRow label={labelMap.winRate} value={getKpiValue('winRate').toFixed(2)} unit="%" />
                    <Divider />
                    <StatRow label={labelMap.profitAmount} value={getKpiValue('profitAmount').toFixed(2)} unit="$" color={getKpiValue('profitAmount') >= 0 ? ORANGE_PRIMARY : "error"} />
                  </Box>
                </Fade>
              )}
              {activeTab === "risk" && (
                <Fade in timeout={400}>
                  <Box>
                    <StatRow label={labelMap.maxDrawdownPct} value={Math.abs(getKpiValue('maxDrawdownPct')).toFixed(2)} unit="%" color={ORANGE_PRIMARY} />
                    <Divider />
                    <StatRow label={labelMap.sharpeRatio} value={getKpiValue('sharpeRatio').toFixed(2)} />
                    <Divider />
                    <StatRow label={labelMap.sortinoRatio} value={getKpiValue('sortinoRatio').toFixed(2)} />
                    <Divider />
                    <StatRow label={labelMap.profitFactor} value={getKpiValue('profitFactor').toFixed(2)} color={getKpiValue('profitFactor') >= 1 ? ORANGE_PRIMARY : "error"} />
                  </Box>
                </Fade>
              )}
              {activeTab === "details" && (
                <Fade in timeout={400}>
                  <Box>
                    <StatRow label={labelMap.trades} value={getKpiValue('trades')} />
                    <Divider />
                    <StatRow label={labelMap.profitLossRatio} value={getKpiValue('profitLossRatio').toFixed(2)} />
                    <Divider />
                    <StatRow label={labelMap.maxConsecutiveLosses} value={getKpiValue('maxConsecutiveLosses')} color="error" />
                    <Divider />
                    <StatRow label={labelMap.maxSingleLoss} value={getKpiValue('maxSingleLoss').toFixed(2)} unit="$" color={ORANGE_PRIMARY} />
                  </Box>
                </Fade>
              )}
            </Box>
          </ModernChartCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <ModernChartCard title={LANGUAGE.Main.Content11} autoHeight>
            <Box height={280} position="relative" display="flex" justifyContent="center" alignItems="center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={winLossData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={75}
                    outerRadius={105}
                    paddingAngle={8}
                    stroke="none"
                  >
                    <Cell fill={ORANGE_PRIMARY} />
                    <Cell fill="#fdece3" />
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <Box position="absolute" textAlign="center">
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  {LANGUAGE.Main.Content1}
                </Typography>
                <Typography variant="h3" fontWeight="bold" color="dark">
                  {winRate.toFixed(0)}%
                </Typography>
              </Box>
            </Box>
            <Box display="flex" justifyContent="center" gap={4} mt={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: ORANGE_PRIMARY }} />
                <Typography variant="button" fontWeight="bold" color="dark">{LANGUAGE.Main.Content1}</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#fdece3" }} />
                <Typography variant="button" fontWeight="bold" color="dark">{LANGUAGE.Main.Content2}</Typography>
              </Box>
            </Box>
          </ModernChartCard>
        </Grid>

        {/* Row 3: Cumulative Return Chart */}
        <Grid item xs={12}>
          <ModernChartCard title={LANGUAGE.Main.Content9} height={350}>
            <AreaChart data={returnData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="colorReturnMod" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={ORANGE_PRIMARY} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={ORANGE_PRIMARY} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#8392ab" }} dy={10} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#8392ab" }} />
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.grayLightest} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="value" stroke={ORANGE_PRIMARY} fillOpacity={1} fill="url(#colorReturnMod)" strokeWidth={2.5} />
            </AreaChart>
          </ModernChartCard>
        </Grid>

        {/* Row 4: 期末已平倉餘額（標題原為「本金變化曲線」，2026-09-29 改名；見上方 equity 的註解）
            有初始本金：範圍柱 [本金, 餘額] ＋ 本金虛線；Y 軸仍含 0，柱長對本金線的比例＝相對本金的漲跌幅。
            沒有初始本金：柱從 0 起算、中性灰、不畫基準線。
            ReferenceLine 放在 Bar 後面 → 虛線畫在柱子上層，基準位置不會被柱子蓋住；線的說明放在標題下方圖例（見 EquitySubtitle）。 */}
        <Grid item xs={12}>
          <ModernChartCard
            title={LANGUAGE.Main.Content10}
            subtitle={<EquitySubtitle caption={equityCaption} legend={equityLegend} />}
            height={350}
          >
            <BarChart data={equity.bars} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.grayLightest} />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#8392ab" }} dy={10} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#8392ab" }} tickFormatter={(v) => formatAxisUsd(v, equityAxisUnit)} />
              {/* 游標底色用淺灰：預設 #ccc 會把中性灰柱（拿不到本金時）蓋到看不見 */}
              <Tooltip cursor={{ fill: COLORS.grayLightest }} content={<EquityTooltip texts={equityTooltipTexts} />} />
              {/* minPointSize：餘額很接近本金（例如 +1.99%）時柱只有 1~3px，至少留 2px 讓人看得到、也比較好 hover */}
              <Bar dataKey={hasEquityBase ? "range" : "balance"} radius={[6, 6, 0, 0]} barSize={40} minPointSize={2}>
                {equity.bars.map((bar, index) => (
                  <Cell key={`cell-${index}`} fill={EQUITY_TONE_COLOR[bar.tone]} />
                ))}
              </Bar>
              {hasEquityBase && (
                <ReferenceLine y={equity.capital} stroke={COLORS.grayDark} strokeDasharray="4 4" ifOverflow="extendDomain" />
              )}
            </BarChart>
          </ModernChartCard>
        </Grid>

        {/* Row 5: Max Drawdown */}
        <Grid item xs={12}>
          <ModernChartCard title={LANGUAGE.Main.Content12} height={350}>
            <AreaChart data={maxdrawdownData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="colorDrawdownMod" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={ORANGE_PRIMARY} stopOpacity={0.15} />
                  <stop offset="95%" stopColor={ORANGE_PRIMARY} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#8392ab" }} dy={10} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#8392ab" }} />
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.grayLightest} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="value" stroke={ORANGE_PRIMARY} fillOpacity={1} fill="url(#colorDrawdownMod)" strokeWidth={2} />
            </AreaChart>
          </ModernChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

// Helper for smoother transitions
const Fade = ({ children, in: inProp, timeout = 300 }) => (
  <Box
    sx={{
      transition: `opacity ${timeout}ms ease-in-out`,
      opacity: inProp ? 1 : 0,
    }}
  >
    {children}
  </Box>
);

export default StrategyKPICharts;
